
    import { mapState, mapMutations, mapGetters, mapActions } from 'vuex';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
    import {
        faChevronRight as farChevronRight,
        faChevronLeft as farChevronLeft,
    } from '@fortawesome/pro-regular-svg-icons';

    import {
        faExternalLinkAlt as fasExternalLinkAlt,
    } from '@fortawesome/pro-solid-svg-icons';

    import { jobData } from '@/utils/jobData';
    import datalayer from '@/utils/datalayer';
    import { Bus } from '@/utils/bus';

    import Apply from '@/components/JobView/Apply/Apply';
    import ApplyHeader from '@/components/JobView/Apply/ApplyHeader';
    import JobViewApplyCta from '@/components/JobView/JobViewApplyCta';
    import SigninModal from '@/components/Auth/SigninModal/SigninModal';
    import ApplyOnboardingQuestions from '@/components/JobView/Apply/ApplyOnboardingQuestions';
    import ApplyCustomQuestions from '@/components/JobView/Apply/ApplyCustomQuestions';
    import ApplyMandatoryLogin from '@/components/JobView/Apply/ApplyMandatoryLogin';
    import SeekerResumeUpload from '@/components/Seeker/SeekerResumeUpload';
    import ApplyThankYou from '@/components/JobView/Apply/ApplyThankYou';

    export default {
        components: {
            Apply,
            ApplyHeader,
            JobViewApplyCta,
            SigninModal,
            ApplyOnboardingQuestions,
            ApplyCustomQuestions,
            ApplyMandatoryLogin,
            FontAwesomeIcon,
            SeekerResumeUpload,
            ApplyThankYou,
        },

        data() {
            return {
                initialPosition: 0,
                bodyElement: '',
                resume: null,
                isResumeOpen: true,
                isResumeUploading: false,
                icons: {
                    farChevronRight,
                    farChevronLeft,
                    fasExternalLinkAlt,
                },
            };
        },

        computed: {
            ...mapState({
                $_jobApplication: state => state.jobApplication,
                $_job: state => state.job.job,
                $_tracking: state => state.eventTracking,
                $_me: state => state.me.user,
                $_meResumes: state => state.me.resumes,
            }),

            ...mapGetters('me', {
                $_isOnboarded: 'isOnboarded',
                $_hasRecentResume: 'hasRecentResume',
            }),

            ...mapGetters({
                $_currentAppUrl: 'currentAppUrl',
            }),

            ...mapGetters('jobModal', {
                $_isJobModalOpen: 'isJobModalOpen',
            }),

            offsiteApplyUrl() {
                return `${this.$siteUrl}job-redirect/${this.$_job.uuid}?flow=${this.$_job.flow_enabled === 'FLOW'}&token=${this.$_job.token}&source='button&page_referral_element=${this.$_tracking.page_referral_element}`;
            },

            successApplyRedirectUrl() {
                return `/discover?uuid=${this.$_job.uuid}`;
            },

            isModal() {
                return this.$_isJobModalOpen;
            },
        },

        watch: {
            '$_jobApplication.is_apply_form_open'(value) {
                if (value) {
                    if (this.$mq === 'sm' && process.browser) {
                        this.addBodyOverflow();
                    }
                } else {
                    this.removeBodyOverflow();
                }
            },
        },

        beforeDestroy() {
            this.$_RESET_AUTH_OPTIONS();
            this.removeBodyOverflow();
            this.$_CLOSE_APPLY_OFFSITE_FORM();
            this.$_CLOSE_APPLY_FORM();
            Bus.$off('Teaser::viewport');
        },

        created() {
            this.calcApplyStepsCount();

            this.$_UPDATE_AUTH_OPTIONS({
                canLoginLater: false,
                hideForgotPassword: true,
                registerWithPassword: true,
                registerButtonLabel: 'Next',
                loginButtonLabel: 'Next',
                sitePosition: 'job_apply',
            });
        },

        mounted() {
            if (process.browser && this.$mq !== 'sm') {
                this.scrollToApply();
            }

            this.detectSeekerResumeUpload();
        },

        methods: {
            ...mapMutations('auth', {
                $_UPDATE_AUTH_OPTIONS: 'UPDATE_AUTH_OPTIONS',
                $_RESET_AUTH_OPTIONS: 'RESET_AUTH_OPTIONS',
            }),

            ...mapMutations('jobApplication', {
                $_CLOSE_SIGNIN_MODAL: 'CLOSE_SIGNIN_MODAL',
                $_STORE_APPLY_STEPS_COUNT: 'STORE_APPLY_STEPS_COUNT',
                $_CLOSE_APPLY_OFFSITE_FORM: 'CLOSE_APPLY_OFFSITE_FORM',
                $_CLOSE_APPLY_FORM: 'CLOSE_APPLY_FORM',
            }),

            ...mapActions('job', {
                $_getJob: 'getJob',
            }),

            ...mapActions('eventTracking', {
                $_pushEvent: 'pushEvent',
            }),

            ...mapMutations('me', {
                $_PUT_JOB_EVENT: 'PUT_JOB_EVENT',
            }),

            ...mapMutations('job', {
                $_UPDATE_JOB: 'UPDATE_JOB',
            }),

            detectSeekerResumeUpload() {
                this.isResumeOpen = this.$_me.id && !this.$_hasRecentResume;
            },

            addBodyOverflow() {
                const body = document.getElementsByTagName('body')[0];
                body.style.overflow = 'hidden';
            },

            removeBodyOverflow() {
                const body = document.getElementsByTagName('body')[0];
                body.style.overflow = '';
            },

            onSigninModalClose() {
                this.$_CLOSE_SIGNIN_MODAL();
            },

            showHideApplyButton(isTeaserShown) {
                const jvApply = this.$el;

                if (isTeaserShown && this.initialPosition === 0) {
                    this.$velocity(jvApply, { translateY: '100%' }, {
                        easing: 'easeInOutQuart',
                        duration: 600,
                    });
                    this.initialPosition = '100%';
                } else if (!isTeaserShown && this.initialPosition === '100%') {
                    this.$velocity(jvApply, { translateY: [0, '100%'] }, {
                        easing: 'easeInOutQuart',
                        duration: 600,
                        complete() {
                            jvApply.style = '';
                        },
                    });
                    this.initialPosition = 0;
                }
            },

            calcApplyStepsCount() {
                const baseStep = 1;
                const customQuestions = this.$_job.apply_questions_step_count;
                const total = baseStep + customQuestions;
                this.$_STORE_APPLY_STEPS_COUNT(total);
            },

            closeApplyForm(scrollTopOnClose = true) {
                this.eventJobApplyClose();
                setTimeout(() => {
                    this.$_CLOSE_APPLY_OFFSITE_FORM();
                    this.$_CLOSE_APPLY_FORM();
                    this.detectSeekerResumeUpload();
                }, 500);

                if (scrollTopOnClose) {
                    if (['xl', 'inf'].includes(this.$mq) && this.isModal) {
                        document.getElementById('jvModalBody').scrollTop = 0;
                    } else {
                        this.$velocity(
                            document.body,
                            'scroll',
                            {
                                duration: 600,
                            });
                    }
                }
            },

            continueUploadResumeAndApply() {
                if (this.resume && this.resume.name) {
                    this.$refs.seekerResumeUpload.triggerUpload();
                } else {
                    this.eventJobOffsiteApplyRedirect();
                    window.open(this.offsiteApplyUrl, '_blank');
                }
            },

            onContinueApply() {
                this.eventJobOffsiteApplyRedirect();
                window.open(this.offsiteApplyUrl, '_blank');
                this.$_UPDATE_JOB({ key: 'is_complete_apply', value: true });
            },

            eventJobApplyClose() {
                const data = {
                    site_position: 'job_apply',
                    event: 'job_apply_close',
                    ...jobData(this.$_job),
                };
                datalayer(data);
                this.$_pushEvent(data);
            },

            eventJobOffsiteApplyRedirect() {
                const data = {
                    event: 'job_offsite_apply_redirect',
                    site_position: 'job_apply',
                    ...jobData(this.$_job),
                };
                datalayer(data);
                this.$_pushEvent(data);
                this.$_PUT_JOB_EVENT({ job: this.$_job.uuid, status: 'is_applied' });
            },

            onResumeAdded(file) {
                this.resume = file;
            },

            onResumeUploading() {
                this.isResumeUploading = true;
            },

            onResumeUploaded() {
                setTimeout(() => {
                    this.eventJobOffsiteApplyRedirect();
                    window.open(this.offsiteApplyUrl, '_blank');
                }, 3000);

                setTimeout(() => {
                    if (!this.isModal) {
                        this.applySuccessRedirect();
                    }
                    this.closeApplyForm();
                }, 6000);
            },

            onResumeUploadError() {
                this.isResumeUploading = false;
            },

            onAfterToryLogin(response) {
                this.detectSeekerResumeUpload();
                if (this.$_hasRecentResume) {
                    this.onContinueApply();

                    if (!this.isModal) {
                        this.applySuccessRedirect();
                    } else {
                        this.closeApplyForm();
                    }
                }
            },

            scrollToApply() {
                const options = {
                    duration: 600,
                };

                if (this.isModal) {
                    const modalDiv = document.getElementById('jvModalBody');
                    const targetDiv = modalDiv.querySelector('#jvApply');

                    options.begin = () => {
                        modalDiv.scrollTop = 0;
                    };

                    options.container = modalDiv;
                    options.offset = modalDiv.offsetTop;

                    this.$velocity(
                        targetDiv,
                        'scroll',
                        options,
                    );
                } else {
                    const targetDiv = document.getElementById('jvApply');
                    this.$velocity(
                        targetDiv,
                        'scroll',
                        options,
                    );
                }
            },

            applySuccessRedirect() {
                this.$router.push(this.successApplyRedirectUrl);
            },
        },
    };
